import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore"; 
import { toast } from 'react-toastify';
import { notifySlotQuotaError } from "../../utils/slotQuota.js";
import { db } from "../../firebase-config";
import {getCamaraById, sendMailUIDChange, updateCamaraById} from "../api/Camara.api";
import { getObras } from "../../obras/api/obras.api.js";
import { Obra } from "../../obras/types/Obra";
import { ORIENTATION_DEGREES, ORIENTATION_LABELS } from "../../utils/cameraOrientation.js";

const OBELISCO_COORDS = { lat: -34.603722, lng: -58.381592 }; 

interface Project {
  id?: string;
  Name?: string;
  identifier?: string;
  orientation?: string;
  coordinates?: { lat: number; lng: number } | null;
  camaraUID?: string;
  customerId?: string;
  active?: boolean;
  status?: "Creado" | "Aprobado" | "Terminado" | "Suspendida" | "";
  [key: string]: any;
}

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (project: Project) => void;
  projectIdToEdit: string; 
  customerId: string; 
}

const CamConfig = ({ isOpen, onClose, onSave, projectIdToEdit, customerId }: ProjectModalProps) => {

  console.log("projectIdToEdit", projectIdToEdit);

  const initialProjectState: Project = {
    Name: "",
    identifier: "",
    orientation: "",
    coordinates: OBELISCO_COORDS,
    customerId: customerId, 
  };

  const [project, setProject] = useState<Project>(initialProjectState);
  const [blocked, setBlocked] = useState(false);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [referenceUID, setReferenceUID] = useState<string>("");
  const [obras, setObras] = useState<Obra[]>([]);

  const projectCollection = collection(db, 'usuarios');

  useEffect(() => {
    if (!isOpen || !customerId) return;

    let cancelled = false;
    getObras(customerId)
      .then((data) => {
        if (!cancelled) setObras(data || []);
      })
      .catch((error) => {
        console.error("Error al obtener las obras del cliente:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId]);

  
  // Effect to load project data when modal opens or projectIdToEdit changes
  useEffect(() => {
    const fetchProjectData = async () => {
      if (projectIdToEdit) {
        setBlocked(true);
        try {
          const camara = await getCamaraById(projectIdToEdit);
          
          if (!camara) {
            toast.error("Proyecto no encontrado.");
            onClose();
            return;
          }
          
          let coordinates: { lat: number; lng: number };

          // Aplicamos la misma validación estricta
          if (
            camara.coordinates && 
            typeof camara.coordinates.lat === 'number' &&
            isFinite(camara.coordinates.lat) &&
            typeof camara.coordinates.lng === 'number' &&
            isFinite(camara.coordinates.lng)
          ) {
            coordinates = camara.coordinates as { lat: number; lng: number };
          } else {
            coordinates = OBELISCO_COORDS; 
            toast.info("Coordenadas no válidas. Mapa centrado por defecto en el Obelisco.", {
              toastId: 'invalid-coords-info', 
              autoClose: 5000,
          })}

            setDocumentId(camara.id); 
            setProject({ 
                ...camara, 
                coordinates: coordinates 
            });


        } catch (error) {
          console.error("Error al cargar datos del proyecto:", error);
          toast.error("Error al cargar los datos del proyecto.");
        } finally {
          setBlocked(false);
        }
      } else {
        setProject(initialProjectState);
        setDocumentId(null);
      }
    };

    if (isOpen) {
      fetchProjectData();
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, projectIdToEdit, onClose]); 




  const fieldMetadata: Record<string, { label: string; description: string; type?: 'text' | 'number' | 'boolean' | 'select'; options?: string[], readOnly?: boolean }> = {
    Name: { label: "Nombre", description: "Nombre del proyecto", type: 'text' },
    orientation: { label: "Orientación (grados de rotación)", description: "Orientación de la cámara", type: 'select', options: ORIENTATION_DEGREES },
    
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (e.target instanceof HTMLInputElement && type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setProject(prev => ({ ...prev, [name]: target.checked }));
      return;
    }
  
    setProject(prev => ({ ...prev, [name]: value }));
  };



  useEffect(() => {
    console.log("project", project);
  }, [project]);



  const handleSubmit = async () => {

    if (!documentId) {
      toast.error("No se pudo encontrar el ID del documento para actualizar.");
      return;
    }
    
    setBlocked(true);
    const payload = { ...project, obra: project.obra || null };
    console.log("Updating project with data:", payload);

    try {
      const response = await updateCamaraById(documentId, payload);
      console.log("Response from updateCamaraById:", response);

      if (!response) {
        toast.error("No se pudo actualizar el proyecto.");
        return;
      }

      toast.success('Proyecto actualizado con éxito.');
      onClose();
      setBlocked(false);
        
    } catch (error) {
      console.error("Error al actualizar el proyecto:", error);
      if (notifySlotQuotaError(error, toast)) return;
      toast.error("Hubo un error al actualizar el proyecto");
    }
  };



  if (!isOpen) return null;



  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative bg-white rounded-lg w-full max-w-4xl mt-16 mb-8">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <p><strong>Editar Cámara</strong></p>
          <p>Aquí puedes actualizar la información de tu cámara.</p>
        </div>
        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {[
            "Name",
            "orientation",
            "obra"
         ].map((name) => {
            const metadata = fieldMetadata[name];
            if (name === 'obra') {
                return (
                    <div key={name}>
                        <label className="block font-medium text-sm mb-1">Obra</label>
                        <select
                            name="obra"
                            value={project.obra || ""}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                            <option value="">Sin obra asignada</option>
                            {obras.map((obra) => (
                                <option key={obra.id} value={obra.id}>{obra.nombre}</option>
                            ))}
                        </select>
                    </div>
                );
            }
            if (metadata?.type === 'select' && metadata.options && name === 'orientation') {
                return (
                    <div key={name}>
                        <label className="block font-medium text-sm mb-1">{metadata.label}</label>
                        <select
                            name={name}
                            value={project[name] || ""}
                            onChange={handleChange}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                            <option value="">Seleccione una orientación</option>
                            {metadata.options.map(option => (
                                <option key={option} value={option}>{ORIENTATION_LABELS[option] || option}</option>
                            ))}
                        </select>
                    </div>
                );
            }
            
            
            return (
              <div key={name}>
                <label className="block font-medium text-sm mb-1">{metadata?.label || name}</label>
                <input
                  type={name === 'responsibleUser' ? 'text' : metadata?.type === 'number' ? 'number' : 'text'}
                  name={name}
                  value={name === 'responsibleUser' && project[name] === 0 ? "" : project[name] || ""} 
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  {...(metadata?.readOnly ? { readOnly: true, disabled: true } : {})}
                />
              </div>
            )
        })}

          <div className="flex justify-end space-x-2 pt-4">
            <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
              Cancelar
            </button>
            <button
                onClick={handleSubmit}
                className={`px-4 py-2 ${blocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'} text-white rounded`}
                disabled={blocked}
                >
                {blocked ? "Guardando..." : "Guardar Cambios"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CamConfig;